import React, { useEffect, useMemo, useRef, useState } from "react";
import cropStyles from "./CropImageUpload.module.scss";
import formStyles from "./manageBlog.module.scss";

const fileToImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const srcToImage = (src) =>
  new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error("Image source missing"));
      return;
    }
    const img = new Image();
    if (typeof src === "string" && !src.startsWith("data:")) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const dataURLtoFile = (dataurl, filename) => {
  const arr = dataurl.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new File([u8arr], filename, { type: mime });
};

/**
 * Handles selecting and cropping an image before returning the cropped output.
 * Props:
 * - aspect: number (aspect ratio to enforce)
 * - cropSize: { width: number, height: number } optional canvas size
 * - onCropped: function({ dataUrl, file })
 * - onClear: function() optional
 * - compact: boolean — renders a small "+" icon trigger instead of the native file input row
 */
const CropImageUpload = ({ aspect, cropSize, onCropped, onClear, compact }) => {
  const inputRef = useRef(null);
  const cropAreaRef = useRef(null);
  const dragState = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });

  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropSourceUrl, setCropSourceUrl] = useState("");
  const [cropZoom, setCropZoom] = useState(1);
  const [cropPos, setCropPos] = useState({ x: 0, y: 0 });
  const [cropNaturalSize, setCropNaturalSize] = useState({ w: 0, h: 0 });
  const [cropAreaSize, setCropAreaSize] = useState({ w: 0, h: 0 });
  const aspectRatio = Number(aspect ?? 1.91);
  const targetAspectRatio = useMemo(() => {
    if (cropSize?.width && cropSize?.height) {
      return Number(cropSize.width) / Number(cropSize.height);
    }
    return aspectRatio;
  }, [aspectRatio, cropSize?.height, cropSize?.width]);

  const frameSize = useMemo(() => {
    const areaW = cropAreaSize.w;
    const areaH = cropAreaSize.h;
    if (!areaW || !areaH) return { width: 0, height: 0 };
    let frameW = areaW;
    let frameH = frameW / targetAspectRatio;
    if (frameH > areaH) {
      frameH = areaH;
      frameW = frameH * targetAspectRatio;
    }
    return { width: frameW, height: frameH };
  }, [cropAreaSize.h, cropAreaSize.w, targetAspectRatio]);

  const frameOffset = useMemo(
    () => ({
      x: (cropAreaSize.w - frameSize.width) / 2 || 0,
      y: (cropAreaSize.h - frameSize.height) / 2 || 0,
    }),
    [cropAreaSize.h, cropAreaSize.w, frameSize.height, frameSize.width]
  );

  useEffect(() => {
    if (!cropperOpen) return undefined;
    const measure = () => {
      if (!cropAreaRef.current) return;
      const rect = cropAreaRef.current.getBoundingClientRect();
      setCropAreaSize({ w: rect.width, h: rect.height });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [cropperOpen]);

  useEffect(() => {
    setCropZoom((prev) => Math.max(getMinCropZoom(), prev));
    setCropPos((prev) => clampCropPosition(prev));
  }, [frameSize.height, frameSize.width]);

  const getMinCropZoom = () => {
    if (!frameSize.width || !cropNaturalSize.w) return 1;
    return Math.max(
      frameSize.width / cropNaturalSize.w,
      frameSize.height / cropNaturalSize.h
    );
  };

  const clampCropPosition = (pos, zoomVal = cropZoom) => {
    if (!frameSize.width || !cropNaturalSize.w) return pos;
    const imgW = cropNaturalSize.w * zoomVal;
    const imgH = cropNaturalSize.h * zoomVal;
    const minX = frameOffset.x + frameSize.width - imgW;
    const minY = frameOffset.y + frameSize.height - imgH;
    const maxX = frameOffset.x;
    const maxY = frameOffset.y;
    return {
      x: Math.min(maxX, Math.max(minX, pos.x)),
      y: Math.min(maxY, Math.max(minY, pos.y)),
    };
  };

  const resetCropperState = () => {
    setCropperOpen(false);
    setCropSourceUrl("");
    setCropZoom(1);
    setCropPos({ x: 0, y: 0 });
    setCropNaturalSize({ w: 0, h: 0 });
    setCropAreaSize({ w: 0, h: 0 });
    dragState.current = {
      active: false,
      pointerId: null,
      startX: 0,
      startY: 0,
      originX: 0,
      originY: 0,
    };
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const img = await fileToImage(file);
      setCropZoom(1);
      setCropPos({ x: 0, y: 0 });
      setCropNaturalSize({ w: 0, h: 0 });
      setCropSourceUrl(img.src);
      setCropperOpen(true);
    } catch (error) {
      console.error(error);
      alert("Unable to load the selected image for cropping.");
    }
  };

  const handleCropImageLoaded = (ev) => {
    if (!ev?.target) return;
    const naturalW = ev.target.naturalWidth;
    const naturalH = ev.target.naturalHeight;
    setCropNaturalSize({ w: naturalW, h: naturalH });
    const rect = cropAreaRef.current?.getBoundingClientRect();
    if (!rect) return;
    const nextArea = { w: rect.width, h: rect.height };
    setCropAreaSize(nextArea);
    const frameW = frameSize.width || nextArea.w;
    const frameH = frameSize.height || nextArea.h;
    const minZoom = Math.max(frameW / naturalW || 1, frameH / naturalH || 1);
    const startZoom = Math.max(minZoom, 1);
    setCropZoom(startZoom);
    setCropPos({
      x:
        frameOffset.x +
        (frameW - naturalW * startZoom) / 2,
      y:
        frameOffset.y +
        (frameH - naturalH * startZoom) / 2,
    });
  };

  const handleCropPointerDown = (e) => {
    e.preventDefault();
    const pointerId = e.pointerId;
    dragState.current = {
      active: true,
      pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: cropPos.x,
      originY: cropPos.y,
    };
    e.currentTarget.setPointerCapture?.(pointerId);
  };

  const handleCropPointerMove = (e) => {
    if (!dragState.current.active) return;
    e.preventDefault();
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    const nextPos = clampCropPosition(
      {
        x: dragState.current.originX + dx,
        y: dragState.current.originY + dy,
      },
      cropZoom
    );
    setCropPos(nextPos);
  };

  const stopCropDragging = (e) => {
    if (!dragState.current.active) return;
    if (e && dragState.current.pointerId) {
      e.currentTarget.releasePointerCapture?.(dragState.current.pointerId);
    }
    dragState.current = {
      active: false,
      pointerId: null,
      startX: 0,
      startY: 0,
      originX: 0,
      originY: 0,
    };
  };

  const handleCropZoomChange = (nextZoomRaw) => {
    const minZoom = getMinCropZoom();
    const nextZoom = Math.max(minZoom, nextZoomRaw);
    if (!cropAreaSize.w || !cropNaturalSize.w) {
      setCropZoom(nextZoom);
      return;
    }

    const centerX = cropAreaSize.w / 2;
    const centerY = cropAreaSize.h / 2;
    const relX = (centerX - cropPos.x) / cropZoom;
    const relY = (centerY - cropPos.y) / cropZoom;
    let nextPos = {
      x: centerX - relX * nextZoom,
      y: centerY - relY * nextZoom,
    };
    nextPos = clampCropPosition(nextPos, nextZoom);
    setCropZoom(nextZoom);
    setCropPos(nextPos);
  };

  const applyCrop = async () => {
    if (!cropSourceUrl || !cropAreaRef.current) return;
    try {
      const img = await srcToImage(cropSourceUrl);
      const frameW = frameSize.width || cropAreaRef.current.clientWidth;
      const frameH = frameSize.height || cropAreaRef.current.clientHeight;
      const frameLeft = frameOffset.x;
      const frameTop = frameOffset.y;
      const scale = cropZoom;
      const sx = (frameLeft - cropPos.x) / scale;
      const sy = (frameTop - cropPos.y) / scale;
      const sw = frameW / scale;
      const sh = frameH / scale;
      const targetW = cropSize?.width || Math.round(frameW);
      const targetH = cropSize?.height || Math.round(frameH);
      const canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
      const croppedFile = dataURLtoFile(
        dataUrl,
        `crop-${Date.now()}.jpg`
      );
      onCropped?.({ dataUrl, file: croppedFile });
      resetCropperState();
    } catch (error) {
      console.error(error);
      alert("Unable to crop the image. Please try again.");
    }
  };

  const handleClear = () => {
    resetCropperState();
    onClear?.();
  };

  return (
    <>
      {compact ? (
        <div
          className={cropStyles.compactTrigger}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <span className={cropStyles.compactIcon}>+</span>
        </div>
      ) : (
        <div className={formStyles.uploadRow}>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className={formStyles.chooseFile}
            style={{ cursor: "pointer" }}
            onChange={handleFileChange}
          />
          <button
            type="button"
            className={formStyles.clearBtn}
            onClick={handleClear}
          >
            Clear
          </button>
        </div>
      )}

      {cropperOpen && (
        <div className={cropStyles.cropOverlay}>
          <div className={cropStyles.cropModal}>
            <div className={cropStyles.cropHeader}>
              <div>
                <div className={cropStyles.cropTitle}>Crop image</div>
                <div className={cropStyles.cropSub}>
                  Fixed {aspectRatio.toFixed(2)} aspect. Drag to position the crop.
                </div>
              </div>
              <button
                type="button"
                className={cropStyles.cropCancel}
                onClick={handleClear}
              >
                Cancel
              </button>
            </div>

            <div className={cropStyles.cropAreaWrap}>
              <div
                className={cropStyles.cropArea}
                ref={cropAreaRef}
                style={{ aspectRatio }}
                onPointerDown={handleCropPointerDown}
                onPointerMove={handleCropPointerMove}
                onPointerUp={stopCropDragging}
                onPointerLeave={stopCropDragging}
                onPointerCancel={stopCropDragging}
              >
                {cropSourceUrl && (
                  <img
                    src={cropSourceUrl}
                    alt="Crop preview"
                    onLoad={handleCropImageLoaded}
                    style={{
                      transform: `translate(${cropPos.x}px, ${cropPos.y}px) scale(${cropZoom})`,
                      transformOrigin: "top left",
                    }}
                    draggable={false}
                  />
                )}
                <div
                  className={cropStyles.cropMask}
                  style={{
                    "--frame-w": frameSize.width ? `${frameSize.width}px` : "100%",
                    "--frame-h": frameSize.height
                      ? `${frameSize.height}px`
                      : "100%",
                  }}
                />
                <div
                  className={cropStyles.cropFrame}
                  style={{
                    "--frame-w": frameSize.width ? `${frameSize.width}px` : "100%",
                    "--frame-h": frameSize.height
                      ? `${frameSize.height}px`
                      : "100%",
                  }}
                />
                <div className={cropStyles.cropGuide}>
                  <span className={cropStyles.cropGuideIcon}>↕︎</span>
                  Drag to reposition the image
                </div>
              </div>
            </div>

            <div className={cropStyles.cropControls}>
              <label className={cropStyles.cropLabel} htmlFor="cropZoom">
                Zoom
              </label>
              <input
                id="cropZoom"
                type="range"
                min={getMinCropZoom()}
                max={Math.max(getMinCropZoom() * 4, getMinCropZoom() + 1)}
                step="0.01"
                value={Math.max(cropZoom, getMinCropZoom())}
                onChange={(e) => handleCropZoomChange(Number(e.target.value))}
                className={cropStyles.cropZoom}
              />
              <div className={cropStyles.cropHint}>
                Drag the image to frame your crop.
              </div>
            </div>

            <div className={cropStyles.cropActions}>
              <button
                type="button"
                className={cropStyles.cropCancel}
                onClick={handleClear}
              >
                Cancel
              </button>
              <button
                type="button"
                className={cropStyles.cropSave}
                onClick={applyCrop}
              >
                Save crop
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CropImageUpload;
